import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { AuthContext } from '../context/AuthContext';
import { useSwipeDismiss } from '../hooks/useSwipeDismiss';
import { useModalA11y } from '../hooks/useModalA11y';

// Dark-aware sticky header. On the marketing homepage when scrolled to the
// very top, the header is transparent + white-on-dark so it reads on the
// dark hero. Once the user scrolls (or on any other page), it switches to
// the standard solid white chrome. The transition is on the bg + text
// colors so it slides between modes smoothly.

export const Header = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { handleProps: swipeHandleProps, sheetStyle: swipeSheetStyle } = useSwipeDismiss(() => setMobileMenuOpen(false));
  const sheetRef = useModalA11y(() => setMobileMenuOpen(false), mobileMenuOpen);

  // Only attach the scroll listener on the homepage — the transparent
  // hero treatment is the only place `scrolled` is read.
  const isHomePath = location.pathname === '/';
  useEffect(() => {
    if (!isHomePath) {
      setScrolled(false);
      return undefined;
    }
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHomePath]);

  // Close the mobile sheet on route change so deep navigation doesn't
  // leave it hovering open behind the new page (mirrors the platform
  // bottom-nav behavior). useModalA11y handles ESC + body-scroll-lock.
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowMenu(false);
  };

  // Whole site is dark now. Only the homepage hero gets the fully
  // transparent header pre-scroll; everything else gets the standard
  // dark-glass header.
  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled && !mobileMenuOpen;

  const bg = transparent
    ? 'bg-transparent border-transparent'
    : 'bg-night/80 backdrop-blur border-line';
  const txt = 'text-fg';
  const navTxt = 'text-fg-muted hover:text-fg';
  const signinBtn = 'border-line-strong text-fg hover:border-acid hover:text-acid';

  return (
    <header className={`sticky top-0 z-50 border-b transition-colors duration-300 ${bg}`}>
      {/* Skip-to-content link for keyboard users — visually hidden until
          focused. Without this, every keyboard tab through every page
          starts by stepping past the entire sticky nav. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-acid focus:text-gray-900 focus:px-3 focus:py-1.5 focus:rounded-lg focus:font-semibold"
      >
        Skip to main content
      </a>
      <div
        className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8"
        style={{
          paddingLeft: 'max(1rem, env(safe-area-inset-left))',
          paddingRight: 'max(1rem, env(safe-area-inset-right))',
        }}
      >
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className={`text-2xl font-bold flex items-center gap-1.5 ${txt}`}>
            OwnDecks
            <span className="inline-block w-2 h-2 bg-acid rounded-full" aria-hidden="true" />
          </Link>

          {/* Desktop Navigation — Scene / Find artists / Crews are
              logged-in-only entry points into the platform. Logged-out
              visitors get a marketing-only header (Logo + Sign in +
              Get started) so internal browse surfaces aren't advertised
              before sign-up. */}
          <nav className="hidden md:flex items-center gap-8">
            {user && (
              <>
                <Link to="/dublin" className={`font-medium transition-colors ${navTxt}`}>
                  Scene
                </Link>
                {/* "Find artists" → /search is organiser-only. Artists
                    landing on the public Header (e.g. /terms,
                    /privacy) shouldn't see a nav entry that bounces
                    them through ProtectedRoute. */}
                {user.userType === 'organizer' && (
                  <Link to="/search" className={`font-medium transition-colors ${navTxt}`}>
                    Find artists
                  </Link>
                )}
                <Link to="/crews" className={`font-medium transition-colors ${navTxt}`}>
                  Crews
                </Link>
                {user.userType === 'artist' && (
                  <Link to="/dashboard" className={`font-medium transition-colors ${navTxt}`}>
                    Dashboard
                  </Link>
                )}
                {user.userType === 'organizer' && (
                  <Link to="/organizer-bookings" className={`font-medium transition-colors ${navTxt}`}>
                    My Bookings
                  </Link>
                )}
              </>
            )}
          </nav>

          {/* User Menu / Auth */}
          <div className="flex items-center gap-3">
            {!user ? (
              <div className="hidden md:flex gap-3">
                <Link to="/login" className={`px-4 py-2 font-medium border rounded-lg transition ${signinBtn}`}>
                  Sign in
                </Link>
                <Link to="/register" className="px-4 py-2 bg-acid text-gray-900 rounded-lg hover:bg-acid-400 font-semibold transition">
                  Get started
                </Link>
              </div>
            ) : (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center gap-2 px-4 py-2 border border-line-strong rounded-lg hover:border-line-strong text-fg font-medium transition"
                  aria-haspopup="menu"
                  aria-expanded={showMenu}
                  aria-label={`Account menu — ${user.email}`}
                >
                  👤 {user.email.split('@')[0]}
                  <span className="text-xs">▼</span>
                </button>

                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-night-50 border border-line rounded-lg shadow-2xl z-50 animate-slide-down">
                    {user.userType === 'artist' ? (
                      <Link
                        to="/dashboard"
                        onClick={() => setShowMenu(false)}
                        className="block px-4 py-3 text-fg hover:bg-glass font-medium"
                      >
                        Dashboard
                      </Link>
                    ) : (
                      <Link
                        to="/organizer-bookings"
                        onClick={() => setShowMenu(false)}
                        className="block px-4 py-3 text-fg hover:bg-glass font-medium"
                      >
                        My Bookings
                      </Link>
                    )}

                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-red-400 hover:bg-glass font-medium border-t border-line"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              className={`md:hidden w-11 h-11 flex items-center justify-center text-2xl ${txt}`}
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — bottom sheet rather than an inline expansion of
          the sticky header. Inline expansion was causing reachability
          issues: when the menu opened, the header grew to fit, but
          page-scrolling didn't move the menu items into the visible
          viewport (sticky headers stay anchored). A portaled
          fixed-position sheet sidesteps the issue entirely — the menu
          is always over the page, never clipped, always thumb-reachable
          from the bottom of the screen. */}
      {mobileMenuOpen && createPortal(
        <>
          {/* Backdrop is its own fixed element so it can never compete
              with the sheet for flex layout. Heavy 85% opacity + blur
              so page content behind doesn't bleed through the sheet —
              60% was too see-through on busy pages. Tap to dismiss. */}
          <div
            className="md:hidden fixed inset-0 bg-night/85 backdrop-blur-sm z-[60] animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Sheet anchored to the viewport bottom directly via
              `bottom-0 left-0 right-0` — no flex `items-end`
              dependency. Earlier version used a flex parent + items-end
              + max-h-[85dvh] on the child; on Samsung Internet the
              `dvh` unit dropped the cap and the sheet filled the
              viewport, leaving only the bottom (the Sign-out footer)
              actually visible. Pinning the sheet by position avoids
              the whole class of bug.
              `vh` is used instead of `dvh` for Samsung Internet
              compatibility — the difference (browser chrome handling)
              is cosmetic. */}
          <div
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            className="md:hidden fixed bottom-0 left-0 right-0 z-[61] bg-night border-t border-line rounded-t-2xl shadow-2xl max-h-[85svh] flex flex-col animate-slide-up"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)', ...swipeSheetStyle }}
          >
            {/* Drag handle — also the swipe-to-dismiss target. py-3
                gives ≥44px of hit area so users can find the gesture
                by feel without seeing exactly where the pill ends. */}
            <div className="py-3 flex justify-center shrink-0 cursor-grab active:cursor-grabbing" {...swipeHandleProps}>
              <div className="w-10 h-1 rounded-full bg-line-strong" />
            </div>

            <div className="flex-1 overflow-y-auto py-2 min-h-0" style={{ overscrollBehavior: 'contain' }}>
              <ul className="divide-y divide-line/60">
                {[
                  // Logged-out visitors get an empty list here — the
                  // sheet just shows the Sign in / Join the scene
                  // footer below. Internal browse surfaces aren't
                  // advertised before sign-up.
                  // "Find artists" → /search is organiser-only; artists
                  // see Scene + Crews + Dashboard.
                  ...(user
                    ? [
                        { to: '/dublin', label: 'Scene' },
                        ...(user.userType === 'organizer'
                          ? [{ to: '/search', label: 'Find artists' }]
                          : []),
                        { to: '/crews',  label: 'Crews' },
                      ]
                    : []),
                  ...(user?.userType === 'artist'
                    ? [{ to: '/dashboard', label: 'Dashboard' }]
                    : user?.userType === 'organizer'
                      ? [{ to: '/organizer-bookings', label: 'My Bookings' }]
                      : []),
                ].map(({ to, label }) => {
                  const active = location.pathname === to;
                  return (
                    <li key={to}>
                      <Link
                        to={to}
                        onClick={() => setMobileMenuOpen(false)}
                        data-autofocus={to === '/dublin' ? '' : undefined}
                        className={`flex items-center justify-between px-5 py-3.5 text-[15px] transition ${
                          active ? 'text-acid font-semibold' : 'text-fg hover:bg-glass'
                        }`}
                      >
                        <span>{label}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-fg-subtle">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="px-5 py-4 border-t border-line shrink-0">
              {user ? (
                <button
                  type="button"
                  onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                  className="w-full px-4 py-2.5 border border-red-500/30 text-red-300 text-sm font-semibold rounded-lg hover:bg-red-500/10"
                >
                  Sign out
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2.5 border border-line-strong text-fg text-sm font-semibold rounded-lg text-center hover:border-acid hover:text-acid"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2.5 bg-acid text-gray-900 text-sm font-semibold rounded-lg text-center hover:bg-acid-400"
                  >
                    Join the scene
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </header>
  );
};
